/**
 * Utilidades para formatear fechas sin problemas de zona horaria
 */

/**
 * Formatea una fecha string (YYYY-MM-DD) a formato local sin problemas de zona horaria
 * @param dateString - Fecha en formato YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss
 * @param locale - Locale para el formateo (por defecto 'es-ES')
 * @param options - Opciones de formateo
 * @returns Fecha formateada
 */
export const formatDateSafe = (
  dateString: string,
  locale: string = 'es-ES',
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) return '';
  
  try {
    // Extraer solo la parte de fecha (YYYY-MM-DD)
    const [year, month, day] = dateString.split('T')[0].split('-');
    
    // Crear fecha local sin problemas de zona horaria
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString(locale, options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Formatea una fecha con formato completo (día de la semana, día, mes, año)
 * @param dateString - Fecha en formato YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss
 * @returns Fecha formateada con formato completo
 */
export const formatDateLong = (dateString: string): string => {
  return formatDateSafe(dateString, 'es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Formatea una fecha con formato corto (DD/MM/YYYY)
 * @param dateString - Fecha en formato YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss
 * @returns Fecha formateada con formato corto
 */
export const formatDateShort = (dateString: string): string => {
  return formatDateSafe(dateString, 'es-ES');
};

/**
 * Formatea una fecha para México (DD/MM/YYYY)
 * @param dateString - Fecha en formato YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss
 * @returns Fecha formateada para México
 */
export const formatDateMX = (dateString: string): string => {
  return formatDateSafe(dateString, 'es-MX');
};

/**
 * Formatea una hora en formato HH:MM eliminando segundos y zona horaria
 * @param timeString - Hora en formato HH:MM:SS, HH:MM:SS.000000Z, o HH:MM
 * @returns Hora formateada como HH:MM
 */
export const formatTime = (timeString: string | null | undefined): string => {
  if (!timeString) return '';
  
  try {
    // Eliminar zona horaria y milisegundos si existen
    // Formatos soportados: "15:00:00.000000Z", "15:00:00", "15:00"
    const cleanTime = timeString.split('.')[0].split('Z')[0];
    
    // Tomar solo HH:MM (primeros 5 caracteres)
    const [hours, minutes] = cleanTime.split(':');
    
    if (hours && minutes) {
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    }
    
    return timeString;
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString || '';
  }
};

/**
 * Formatea un rango de horas
 * @param startTime - Hora de inicio
 * @param endTime - Hora de fin (opcional)
 * @returns Rango de horas formateado como "HH:MM - HH:MM" o solo "HH:MM" si no hay hora de fin
 */
export const formatTimeRange = (
  startTime: string | null | undefined, 
  endTime?: string | null | undefined
): string => {
  const formattedStart = formatTime(startTime);
  
  if (!endTime) {
    return formattedStart;
  }
  
  const formattedEnd = formatTime(endTime);
  return `${formattedStart} - ${formattedEnd}`;
};
